import {
  AcfMediaFragment,
  FeaturedInducteesFragment,
  GetCoverStoryDetailQuery,
  GetInducteesByClassQuery,
  ListInducteesFragment,
  Inductee,
  VideoSingularFragment,
} from "@graphqlTypes";
import Hls from "hls.js";
import { DateTime } from "luxon";

export const isExternalLink = (uri: string): boolean => {
  try {
    const url = new URL(uri);
    return url.origin !== window.location.origin;
  } catch (e) {
    return true;
  }
};

export const isTouchDevice = (): boolean => {
  if (typeof window === "undefined") return false;
  return document.body.classList.contains("touch");
}

export const getPagePath = (uri?: string): string => {
  if (!uri) return "";
  if (isExternalLink(uri)) return uri;
  return new URL(uri, "http://dummy").pathname;
};

export const getHighlightMarkup = (text: string): string => {
  if (!text) return "";
  return text?.replace(
    /\*\*(.+?)\*\*/g,
    '<span class="highlighted-text">$1</span>'
  );
};

export const removeHyphens = (text: string): string => {
  if (!text) return "";
  return text.replace(/-/g, " ");
};

export const getInducteeCategoryLabel = (group: string, labels: GlobalLabels) => {
  const copyKey = Object.keys(labels?.listingInducteesCopy || {}).find((label) => {
    const groupLabel = group?.replace(/-/g, '').toLowerCase();
    return label.toLowerCase() === groupLabel;
  });
  const groupCopy = copyKey ? labels?.listingInducteesCopy[copyKey as keyof typeof labels.listingInducteesCopy] : null;
  if (typeof groupCopy !== 'object') return null;
  return groupCopy;
}

export const getUrlLocale = (url: string, fallback = true): string | null => {
  const match = url?.match(/^\/(en|es)(\/|$)/);
  if (match) {
    return match[1];
  }
  return fallback ? 'en' : null;
};

export const isUrlInternal = (url: string): boolean => {
  if (!url) return false;
  try {
    const linkUrl = new URL(url, window.location.origin);
    return linkUrl.origin === window.location.origin;
  } catch (e) {
    return false;
  }
}

export const localizeURL = (url: string, locale: string): string => {
  if (!isUrlInternal(url)) return url;
  const currentLocale = getUrlLocale(url);
  if (currentLocale === locale || locale === 'en') return url;
  return `/${locale}${url.startsWith('/') ? '' : '/'}${url}`;
};

export const updateBodyScroll = (disable: boolean) => {
  if (typeof window === "undefined") return;
  if (disable) {
    document.body.classList.add("locked");
  } else {
    document.body.classList.remove("locked");
  }
}

export const sortInductees = (
  inductees: ListInductees,
  ignoreSortName = false
): ListInductees => {
  return [...inductees].sort((a, b) => {
    if (ignoreSortName) {
      const titleA = a.title?.toLowerCase() || "";
      const titleB = b.title?.toLowerCase() || "";
      return titleA.localeCompare(titleB);
    }
    const nameA = (a.inducteeData?.sortName?.trim() || a.title || "").toLowerCase();
    const nameB = (b.inducteeData?.sortName?.trim() || b.title || "").toLowerCase();
    return nameA.localeCompare(nameB);
  });
};

export const sortNominees = (
  nominees: ListInducteesFragment["nominees"] = []
): ListInducteesFragment["nominees"] => {
  return [...nominees].sort((a, b) => {
    const nameA = (a.name?.trim()|| "").toLowerCase();
    const nameB = (b.name?.trim()|| "").toLowerCase();
    return nameA.localeCompare(nameB);
  });
}

export const groupInducteesByYear = (
  inductees: ListInductees
): Record<string, ListInductees> => {
  return inductees.reduce((acc, inductee) => {
    const year =
      inductee.inducteeData?.inducteeClass[0]?.slug || "Unknown Year";
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(inductee);
    return acc;
  }, {} as Record<string, ListInductees>);
};

export const groupInducteesByType = (
  inductees: GetInducteesByClassQuery["relatedInductees"]["nodes"]
): Record<string, ListInductees> => {
  return inductees.reduce((acc, inductee) => {
    const [type] = inductee.inducteeData?.category || [];
    if (!type) return acc;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(inductee);
    return acc;
  }, {} as Record<string, ListInductees>);
};

export const scaleVal = (value: number, invert = 1) => {
  if (typeof window === "undefined") return value;
  const vw = window.innerWidth / 100;
  let target = 390;
  if (window.innerWidth >= 768) target = 834;
  if (window.innerWidth > 1024) target = 1440;
  return Math.floor((value / target) * 100 * vw * invert);
};

export const getTextWidth = (text: string, font?: string): number => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  context.font = font || getComputedStyle(document.body).font;
  return context.measureText(text).width;
};

export const getMuxUrl = (id: string) => `https://stream.mux.com/${id}.m3u8`;
export const getVideoPoster = (id: string) =>
  `https://image.mux.com/${id}/thumbnail.webp?time=0`;

export const attachHlsToVideo = (
  videoEl: HTMLVideoElement,
  assetId: string
) => {
  const assetUrl = getMuxUrl(assetId);
  if (videoEl.canPlayType("application/vnd.apple.mpegurl")) {
    videoEl.src = assetUrl;
  } else if (Hls.isSupported()) {
    const hls = new Hls();
    hls.loadSource(assetUrl);
    hls.attachMedia(videoEl);
  } else {
    console.error("HLS is not supported in this browser");
  }
};

export const getCopyLeftMargin = (index: number) => {
  if (index === 0) return "";
  if (index === 1) return "10rem";
  return "4rem";
};

export const getDateRange = (startDate: string, endDate?: string): string => {
  const start_date = DateTime.fromISO(startDate, {
    setZone: true,
    zone: "America/New_York",
  });
  const startDateFormatted = start_date.toFormat("MMM d");
  const end_date = endDate
    ? DateTime.fromISO(endDate, { setZone: true, zone: "America/New_York" })
    : start_date;
  const endDateFormat = start_date.month === end_date.month ? "d" : "MMM d";
  const endDateFormatted = endDate ? end_date.toFormat(endDateFormat) : "";
  const sameDay = endDate && start_date.hasSame(end_date, "day");
  const startFormat = start_date.minute === 0 ? "h" : "h:mm";
  const startTime = start_date.toFormat(startFormat);
  const startMeridiem = start_date.toFormat("a");
  if (sameDay) {
    const endFormat = end_date.minute === 0 ? "h" : "h:mm";
    const endTime = end_date.toFormat(endFormat);
    const endMeridiem = end_date.toFormat("a");
    if (startMeridiem === endMeridiem) {
      return `${startDateFormatted}, ${startTime}-${endTime}${endMeridiem}`;
    }
    return `${startDateFormatted}, ${startTime}${startMeridiem}-${endTime}${endMeridiem}`;
  } else if (endDate) {
    return `${startDateFormatted}-${endDateFormatted}`;
  }
  return `${startDateFormatted}, ${startTime}${startMeridiem}`;
};

export const getEventTypeCopy = (
  eventType: string,
  price?: string
): { main: string; sub?: string } => {
  switch (eventType) {
    case "free":
      return { main: "Free" };
    case "free-with-admission":
      return { main: "Free", sub: "with admission" };
    case "free-with-rsvp":
      return { main: "Free", sub: "with RSVP" };
    case "ticketed":
      return { main: "Ticketed" };
    default:
      return { main: "Unknown event type", sub: eventType };
  }
};

export const formatDate = (
  date: string,
  format: string = "MMM d, yyyy"
): string => {
  const dt = DateTime.fromISO(date, {
    setZone: true,
    zone: "America/New_York",
  });
  return dt.isValid ? dt.toFormat(format) : date;
};

export const ensurePostsAreInductees = (posts: FeaturedInducteesFragment) => {
  return (
    posts.nodes?.filter((post): post is Inductee => {
      return post && post.__typename === "Inductee";
    }) || []
  );
};

export const ensurePostsAreRHTVVideos = (posts: RockHallTvChannel['rhtvChannelContent']['rhtvVideos']) => {
   return (
    posts.nodes?.filter((post): post is RockHallTvVideo => {
      return post && post.__typename === "RhtvVideo";
    }) || []
  );
}

export const getDefaultRHTVVideo = (channel: RockHallTvChannel | null, videoSlug?: string | null): RockHallTvVideo | null => {
  const posts = channel?.rhtvChannelContent?.rhtvVideos;
  if (!posts) return null;
  const videos = ensurePostsAreRHTVVideos(posts);
  return (videoSlug && videos.find((video) => video.slug === videoSlug)) || videos[0] || null;
};

const PREVIEW_CHANNEL_SLUG = 'rhtv-preview';

export const mergePreviewChannel = (
  channels: RockHallTvChannel[],
  preview: RockHallTvChannel,
  realSlug?: string | null,
): { channels: RockHallTvChannel[]; channel: RockHallTvChannel } => {
  const channel = { ...preview, slug: realSlug || PREVIEW_CHANNEL_SLUG };
  const index = channels.findIndex((ch) => ch.slug === channel.slug);
  if (index === -1) return { channels: [...channels, channel], channel };
  return { channels: channels.map((ch, i) => (i === index ? channel : ch)), channel };
};

export const removeHtmlTags = (text: string): string => {
  if (!text) return "";
  return text.replace(/<[^>]+>/g, "");
};

// Flattens marked-up copy to a single string for use as an aria-label. Screen
// readers otherwise read a heading as separate fragments once it contains line
// breaks or inline highlight spans.
export const getPlainText = (text?: string | null): string => {
  if (!text) return "";
  return removeHtmlTags(text.replace(/<br\s*\/?>/gi, " ").replace(/&nbsp;/gi, " "))
    .replace(/[*`]/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

export const parseBreaksToParagraphs = (text: string): string[] => {
  if (!text) return [];
  const newText = text.replace(/<p>/, "").replace(/<\/p>/, "");
  const paragraphs = newText.split(/<br \/>(?:\r\n)?/);
  return paragraphs.map((p) => p.trim()).filter((p) => p.length > 0);
};

export const dedupeLineBreaks = (text: string): string => {
  if (!text) return "";
  return text?.replace(/(<br\s*\/?>[\s\r\n]*){2,}/g, "<br />");
}

export const getPageTitle = (siteTitle: string, title?: string): string => {
  return title ? `${title} | ${siteTitle}` : siteTitle;
}

export const checkYoutubeIframeApi = () => {
  if (typeof window === "undefined") return;
  if ((window as any).__rhYtApiAllowed === false) return;
  if (window.YT && window.YT.Player) return;
  const tag = document.createElement("script");
  tag.src = "https://www.youtube.com/iframe_api";

  const firstScriptTag = document.getElementsByTagName("script")[0];
  firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
};

export const removeFilterStyles = (el?: HTMLElement) =>
  new Promise<void>((resolve) => {
    if (!el) {
      resolve();
      return;
    }
    setTimeout(() => {
      el?.style.removeProperty("filter");
      el?.style.removeProperty("opacity");
      resolve();
    }, 500);
  });

export const getCardWidth = (
  totalWidth: number,
  gap: number,
  columns: number
): number => {
  return (totalWidth - gap * (columns - 1)) / columns;
};

export const chunkArray = <T>(array: T[], chunkSize: number): T[][] => {
  if (chunkSize <= 0) return [array];
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

export const replaceStringVar = (
  str: string,
  values: Record<string, string>
): string => {
  let result = str;
  Object.entries(values).forEach(([varName, value]) => {
    const regex = new RegExp(`{{${varName}}}`, "gi");
    result = result?.replace(regex, value || "");
  });
  return result;
};

export const getLinkData = (
  props: {
    linkType?: string[];
    label?: string;
    sectionId?: string;
    page?: { node?: { uri?: string } };
    externalLink?: string;
  },
  noLabel = false
) => {
  if (!props || !props?.linkType) return null;
  const { linkType, label, page, externalLink, sectionId } = props;
  const [type] = linkType || [];
  if (!type || (!label && !noLabel)) return null;
  switch (type) {
    case "internal":
      if (!page?.node?.uri) return null;
      let url = page?.node?.uri;
      if (sectionId && url) url += `#${sectionId}`;
      return { label, url: url || "" };
    case "external":
      if (!externalLink) return null;
      return { label: label, url: externalLink || "", target: "_blank" };
    default:
      return null;
  }
};

export const getDAMProxyLink = (link: string): string => {
  if (!link) return "";
  return link;
  // return link.replace('https://digital.rockhall.com/AssetLink', '/rockhall-assets');
};

export const getBasicQueryVars = ({ databaseId, uri }: SeedNode, { asPreview }: { asPreview?: boolean }) => {
  const language = getUrlLocale(uri);
  return { databaseId, asPreview, language };
};

export const getLanguageQueryVar = ({ uri }: SeedNode) => {
  const language = getUrlLocale(uri);
  return { language };
};

type GetVideoEmbedProps = Omit<VideoSingularFragment, '__typename' | 'headline' | 'videoTitle' | 'eyebrow'>;

export const getYoutubeThumbnail = (videoId: string, fallback = false) =>
  !fallback ? `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`
  : `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

export const getVideoEmbed = (video: GetVideoEmbedProps): {
  videoUrl?: string;
  videoId?: string;
  thumbnail: AcfMediaFragment | null;
  previewVideoUrl?: string;
  isYoutube: boolean;
} | null => {
  if (!video) return null;
  const { videoType, videoUrl, youtubeThumbnail, videoThumbnail, youtubeVideoId, previewVideoUrl } = video;
  switch (videoType?.[0]) {
    case "youtube":
      return youtubeVideoId ? {
        videoId: youtubeVideoId,
        thumbnail: youtubeThumbnail,
        previewVideoUrl,
        isYoutube: true
      } : null;
    case "dam-link":
      return videoUrl ? {
        videoUrl,
        thumbnail: videoThumbnail,
        previewVideoUrl,
        isYoutube: false
      } : null;
    default:
      return null;
  }
};

export const remToPx = (r: number) => {
  return r * parseFloat(getComputedStyle(document.documentElement).fontSize);
};

export const cleanHeadline = (value?: string, fallback = ''): string => (value || fallback).replace(/\*/g, '');

async function getKey(): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(process.env.NEXT_PUBLIC_COVER_STORY_KEY);
  const hashedKey = await crypto.subtle.digest('SHA-256', keyData);
  return await crypto.subtle.importKey(
    'raw',
    hashedKey,
    { name: 'AES-CBC' },
    false,
    ['decrypt']
  );
}

export async function decryptCoverStoryData(encryptedData: string): Promise<string> {
  try {
    const decoded = atob(encryptedData);
    const ivLength = 16;
    const iv = new Uint8Array(Array.from(decoded.substring(0, ivLength)).map(c => c.charCodeAt(0)));
    const ciphertext = new Uint8Array(Array.from(decoded.substring(ivLength)).map(c => c.charCodeAt(0)));
    const key = await getKey();
  
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-CBC', iv },
      key,
      ciphertext
    );
    
    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
  } catch (error) {
    console.error('Decryption error:', error);
    return '';
  }
}

export async function decryptCoverStoryImage(encryptedData: string): Promise<string> {
  return decryptCoverStoryData(encryptedData);
}

export async function decryptCoverStoryAlbum(encryptedData: GetCoverStoryDetailQuery['rhCoverStory']['gameData']['albumFormatted']): Promise<{ artist: string; album: string } | null> {
  return new Promise(async (resolve) => {
    if (!encryptedData) {
      resolve(null);
      return;
    }
    const { artist: encryptedArtist, album: encryptedAlbum } = encryptedData;
    try {
      const [artist, album] = await Promise.all([
        decryptCoverStoryData(encryptedArtist),
        decryptCoverStoryData(encryptedAlbum)
      ]);
      resolve({ artist, album });
    } catch (error) {
      console.error('Error decrypting album data:', error);
      resolve(null);
    }
  });
}